import { escapeHtml, replaceContainerHtml } from '@theme/dom-utils';
import { REVIEWS_CREATE_URL, REVIEWS_LIST_URL } from '@theme/forms-api-config';

const section = document.querySelector('.reviews-section');
if (!section) {
  // no-op entry for pages without reviews
} else {
  const productHandle = section.dataset.productHandle;
  const productTitle = section.dataset.productTitle;
  const isReviewsPage = !productHandle;
  const starEmptyUrl = section.dataset.starEmpty || '/assets/icon_star1.svg';
  const starFilledUrl = section.dataset.starFilled || '/assets/icon_star2.svg';
  const dateLocale = section.dataset.dateLocale || document.documentElement.lang || 'en-US';
  const ratingLabel = section.dataset.ratingLabel || 'Customer Rating';

  const reviewFormPopup = document.getElementById('am-popup-review-form');
  const reviewForm = document.getElementById('review-form');
  const reviewsListContainer = document.getElementById('reviews-list-container');
  const reviewsLoading = document.getElementById('reviews-loading');
  const reviewsEmptyState = document.getElementById('reviews-empty-state');
  const reviewsError = document.getElementById('reviews-error');
  const reviewsCount = document.getElementById('reviews-count');
  const reviewsSectionHeader = document.querySelector('.reviews-section__header');
  const reviewsBottomCta = document.getElementById('reviews-bottom-cta');
  const photoPreviewContainer = document.getElementById('photo-preview-container');
  const photoInput = document.getElementById('review-photos');
  const productRatingStars = document.getElementById('product-rating-stars');
  const productRatingValue = document.getElementById('product-rating-value');
  const combinedRatingInput = document.getElementById('combined-rating');
  const reviewFormError = document.getElementById('review-form-error');
  const submitReviewBtn = document.getElementById('submit-review-btn');
  const reviewsZoomDialog = document.getElementById('reviews-zoom-dialog');

  let productRating = 0;
  let uploadedPhotos = [];
  let reviewCooldownUntil = 0;

  let allPhotos = [];
  const REVIEW_TEXT_LIMIT = 2000;
  const REVIEW_COOLDOWN_MS = 60000;
  const SHORT_FIELD_LIMIT = 120;

  // Utility functions

  function createRemovePhotoIcon() {
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('width', '16');
    svg.setAttribute('height', '16');
    svg.setAttribute('viewBox', '0 0 14 14');
    svg.setAttribute('fill', 'none');
    const path1 = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    path1.setAttribute('d', 'M12 2L2 12');
    path1.setAttribute('stroke', 'currentColor');
    path1.setAttribute('stroke-width', '1.5');
    path1.setAttribute('stroke-linecap', 'round');
    const path2 = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    path2.setAttribute('d', 'M12 12L2 2');
    path2.setAttribute('stroke', 'currentColor');
    path2.setAttribute('stroke-width', '1.5');
    path2.setAttribute('stroke-linecap', 'round');
    svg.appendChild(path1);
    svg.appendChild(path2);
    return svg;
  }

  function formatDate(dateString) {
    const date = new Date(dateString);
    return date.toLocaleDateString(dateLocale, {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  }

  function stripTags(value) {
    return String(value || '').replace(/<[^>]*>/g, '').trim();
  }

  function isReviewCooldownActive() {
    return Date.now() < reviewCooldownUntil;
  }

  function setReviewCooldown(cooldownMs) {
    reviewCooldownUntil = Date.now() + cooldownMs;
    if (!submitReviewBtn) return;
    submitReviewBtn.disabled = true;
    setTimeout(() => {
      if (isReviewCooldownActive()) return;
      submitReviewBtn.disabled = false;
      submitReviewBtn.textContent = 'Submit Review';
    }, cooldownMs);
  }

  function applyReviewFieldLimits() {
    const orderEl = document.getElementById('review-order-number');
    const textEl = document.getElementById('review-text');
    const nameEl = document.getElementById('review-user-name');
    if (orderEl) orderEl.maxLength = SHORT_FIELD_LIMIT;
    if (nameEl) nameEl.maxLength = SHORT_FIELD_LIMIT;
    if (textEl) textEl.maxLength = REVIEW_TEXT_LIMIT;
  }

  function initReviewTextCounter() {
    const textEl = document.getElementById('review-text');
    if (!textEl || textEl.dataset.counterReady === 'true') return;
    
    const section = document.querySelector('.reviews-section');
    const translationTemplate = section?.dataset.charactersRemaining || '{{ count }} characters remaining';
    
    const counterEl = document.createElement('div');
    const updateCounter = () => {
      const remaining = REVIEW_TEXT_LIMIT - textEl.value.length;
      const count = Math.max(remaining, 0);
      counterEl.textContent = translationTemplate.replace('{{ count }}', count);
    };
    counterEl.className = 'am-popup__review-text-counter';
    textEl.insertAdjacentElement('afterend', counterEl);
    textEl.addEventListener('input', updateCounter);
    textEl.dataset.counterReady = 'true';
    updateCounter();
  }

  function parseRetryAfterMs(response, fallbackMs) {
    const retryAfter = Number(response.headers.get('Retry-After'));
    if (Number.isFinite(retryAfter) && retryAfter > 0) {
      return retryAfter * 1000;
    }
    return fallbackMs;
  }

  function renderStars(rating) {
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;
    let starsHtml = '';
    for (let i = 1; i <= 5; i++) {
      if (i <= fullStars) {
        starsHtml += `<img src="${starFilledUrl}" alt="Star" width="19" height="18" style="display: inline-block;">`;
      } else if (i === fullStars + 1 && hasHalfStar) {
        starsHtml += `<img src="${starFilledUrl}" alt="Star" width="19" height="18" style="display: inline-block; opacity: 0.5;">`;
      } else {
        starsHtml += `<img src="${starEmptyUrl}" alt="Star" width="19" height="18" style="display: inline-block;">`;
      }
    }
    return starsHtml;
  }

  function resetForm() {
    if (reviewForm) {
      reviewForm.reset();
    }
    productRating = 0;
    uploadedPhotos = [];
    updateStarDisplay(productRatingStars, 0);
    if (productRatingValue) productRatingValue.value = '';
    if (combinedRatingInput) combinedRatingInput.value = '';
    if (photoPreviewContainer) photoPreviewContainer.replaceChildren();
    updatePhotoInput();
    hideFormError();
  }

  function updateStarDisplay(container, rating) {
    if (!container) return;
    const stars = container.querySelectorAll('.am-popup__review-star');
    stars.forEach((star, index) => {
      const value = index + 1;
      const emptyIcon = star.querySelector('.am-popup__review-star-icon--empty');
      const filledIcon = star.querySelector('.am-popup__review-star-icon--filled');
      if (!emptyIcon || !filledIcon) return;
      if (value <= rating) {
        emptyIcon.style.display = 'none';
        filledIcon.style.display = 'block';
        star.setAttribute('data-selected', 'true');
      } else {
        emptyIcon.style.display = 'block';
        filledIcon.style.display = 'none';
        star.removeAttribute('data-selected');
      }
    });
  }

  function setupStarRating(container) {
    if (!container) return;
    const stars = container.querySelectorAll('.am-popup__review-star');
    stars.forEach((star, index) => {
      const value = index + 1;
      star.addEventListener('click', () => {
        productRating = value;
        if (productRatingValue) productRatingValue.value = value;
        if (combinedRatingInput) combinedRatingInput.value = value;
        updateStarDisplay(container, value);
      });
      star.addEventListener('mouseenter', () => {
        updateStarDisplay(container, value);
      });
      star.addEventListener('mouseleave', () => {
        updateStarDisplay(container, productRating);
      });
      star.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          star.click();
        }
      });
    });
  }

  // Photo upload
  function setupPhotoUpload() {
    if (!photoInput) return;
    photoInput.addEventListener('change', (e) => {
      const files = Array.from(e.target.files);
      handlePhotoFiles(files);
    });
  }

  function handlePhotoFiles(files) {
    const maxSize = 5 * 1024 * 1024; // 5MB
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
    const validFiles = [];
    const errors = [];

    files.forEach((file, index) => {
      if (!allowedTypes.includes(file.type)) {
        errors.push(`File "${file.name}" is not a valid image type`);
        return; // Пропускаем этот файл
      }
      if (file.size > maxSize) {
        errors.push(`Photo "${file.name}" exceeds maximum size (5MB)`);
        return; // Пропускаем этот файл
      }
      validFiles.push(file);
    });

    // Показываем ошибки если есть
    if (errors.length > 0) {
      showFormError(errors.join(', '));
    }

    // Проверяем общее количество фотографий
    const totalPhotos = uploadedPhotos.length + validFiles.length;
    if (totalPhotos > 5) {
      showFormError('Maximum 5 photos allowed');
      uploadedPhotos = [...uploadedPhotos, ...validFiles].slice(0, 5);
    } else {
      uploadedPhotos = [...uploadedPhotos, ...validFiles];
    }
    
    renderPhotoPreviews();
    updatePhotoInput();
  }

  function updatePhotoInput() {
    if (!photoInput) return;
    const dt = new DataTransfer();
    uploadedPhotos.forEach(file => {
      dt.items.add(file);
    });
    photoInput.files = dt.files;
  }

  function renderPhotoPreviews() {
    if (!photoPreviewContainer) return;
    photoPreviewContainer.replaceChildren();
    uploadedPhotos.forEach((file, index) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const preview = document.createElement('div');
        preview.className = 'am-popup__review-photo-preview-item';

        const img = document.createElement('img');
        img.src = e.target.result;
        img.alt = `Preview ${index + 1}`;
        img.loading = 'lazy';

        const removeBtn = document.createElement('button');
        removeBtn.type = 'button';
        removeBtn.className = 'am-popup__review-photo-remove';
        removeBtn.dataset.index = String(index);
        removeBtn.setAttribute('aria-label', 'Remove photo');
        removeBtn.appendChild(createRemovePhotoIcon());
        removeBtn.addEventListener('click', () => {
          uploadedPhotos.splice(index, 1);
          renderPhotoPreviews();
          updatePhotoInput();
        });

        preview.appendChild(img);
        preview.appendChild(removeBtn);
        photoPreviewContainer.appendChild(preview);
      };
      reader.readAsDataURL(file);
    });
  }

  // Form validation
  function validateForm() {
    const orderNumber = document.getElementById('review-order-number').value.trim();
    const text = document.getElementById('review-text').value.trim();
    const userName = document.getElementById('review-user-name').value.trim();
    const honeypot = reviewForm.querySelector('[name="honeypot"]').value;

    if (honeypot && honeypot.trim() !== '') {
      showFormError('Spam detected');
      return false;
    }

    if (!orderNumber) {
      showFormError('Order number is required');
      return false;
    }
    if (!userName) {
      showFormError('User name is required');
      return false;
    }
    if (!text) {
      showFormError('Review text is required');
      return false;
    }

    if (!productRating || productRating === 0) {
      showFormError('Product rating is required');
      return false;
    }

    if (uploadedPhotos.length > 5) {
      showFormError('Maximum 5 photos');
      return false;
    }

    const maxSize = 5 * 1024 * 1024; // 5MB
    for (let i = 0; i < uploadedPhotos.length; i++) {
      if (uploadedPhotos[i].size > maxSize) {
        showFormError(`Photo ${i + 1} exceeds maximum size (5MB)`);
        return false;
      }
    }

    return true;
  }

  function showFormError(message) {
    if (!reviewFormError) return;
    reviewFormError.textContent = message;
    reviewFormError.style.display = 'block';
  }

  function hideFormError() {
    if (!reviewFormError) return;
    reviewFormError.style.display = 'none';
    reviewFormError.textContent = '';
  }

  function collectReviewPayload() {
    const orderNumberEl = document.getElementById('review-order-number');
    const textEl = document.getElementById('review-text');
    const userNameEl = document.getElementById('review-user-name');
    const handleEl = reviewForm.querySelector('[name="handle"]');
    const productNameEl = reviewForm.querySelector('[name="product_name"]');
    const handleFromForm = handleEl ? stripTags(handleEl.value).slice(0, SHORT_FIELD_LIMIT) : '';
    const productNameFromForm = productNameEl ? stripTags(productNameEl.value).slice(0, SHORT_FIELD_LIMIT) : '';

    return {
      orderNumber: orderNumberEl ? stripTags(orderNumberEl.value).slice(0, SHORT_FIELD_LIMIT) : '',
      text: textEl ? stripTags(textEl.value).slice(0, REVIEW_TEXT_LIMIT) : '',
      userName: userNameEl ? stripTags(userNameEl.value).slice(0, SHORT_FIELD_LIMIT) : '',
      honeypot: stripTags(reviewForm.querySelector('[name="honeypot"]')?.value || ''),
      handle: handleFromForm || stripTags(productHandle || '').slice(0, SHORT_FIELD_LIMIT),
      productName: productNameFromForm || stripTags(productTitle || '').slice(0, SHORT_FIELD_LIMIT)
    };
  }

  function validateReviewPayload(values) {
    if (!isReviewsPage && !values.handle?.trim()) return 'Product handle is missing';
    if (!isReviewsPage && !values.productName?.trim()) return 'Product name is missing';
    if (!values.userName?.trim()) return 'User name is required';
    if (!values.orderNumber?.trim()) return 'Order number is required';
    if (!values.text?.trim()) return 'Review text is required';
    if (!productRating || productRating < 1 || productRating > 5) return 'Product rating is required';
    return '';
  }

  function buildReviewSubmitFormData(values) {
    const formData = new FormData();
    if (values.handle?.trim()) formData.append('handle', values.handle.trim());
    if (values.productName?.trim()) formData.append('product_name', values.productName.trim());
    formData.append('user_name', values.userName.trim());
    formData.append('order_number', values.orderNumber.trim());
    formData.append('text', values.text.trim());
    formData.append('product_rating', String(productRating));
    formData.append('rating', String(productRating));
    formData.append('honeypot', values.honeypot);
    uploadedPhotos.forEach((file) => {
      formData.append('photos', file);
    });
    return formData;
  }

  function getMissingReviewFields(formData) {
    const requiredFields = [
      ...(isReviewsPage ? [] : ['handle', 'product_name']),
      'user_name',
      'order_number',
      'text',
    ];
    return requiredFields.filter((field) => {
      const value = formData.get(field);
      return !value || (typeof value === 'string' && value.trim() === '');
    });
  }

  async function submitReviewToOffice(formData) {
    const response = await fetch(REVIEWS_CREATE_URL, {
      method: 'POST',
      body: formData
    });
    const responseText = await response.text();
    let data = {};
    if (responseText) {
      try {
        data = JSON.parse(responseText);
      } catch (error) {
        console.warn('Failed to parse review submission response', error);
      }
    }
    return { response, data };
  }

  async function processReviewSubmit() {
    const values = collectReviewPayload();
    const validationError = validateReviewPayload(values);
    if (validationError) {
      showFormError(validationError);
      return;
    }

    if (combinedRatingInput) combinedRatingInput.value = String(productRating);
    submitReviewBtn.disabled = true;
    submitReviewBtn.textContent = 'Submitting...';

    try {
      const formData = buildReviewSubmitFormData(values);
      const missingFields = getMissingReviewFields(formData);
      if (missingFields.length > 0) {
        showFormError(`Missing required fields: ${missingFields.join(', ')}`);
        return;
      }

      const { response, data } = await submitReviewToOffice(formData);
      if (response.status === 429) {
        showFormError('Too many review submissions. Please wait a minute and try again');
        setReviewCooldown(parseRetryAfterMs(response, REVIEW_COOLDOWN_MS));
        return;
      }
      if (!response.ok) {
        const errorMessage = data.error || data.message || 'Error submitting review';
        const errorDetails = data.details ? (Array.isArray(data.details) ? data.details.join(', ') : data.details) : '';
        throw new Error(errorMessage + (errorDetails ? ': ' + errorDetails : ''));
      }
      if (data.ok) {
        if (typeof window.closePopup === 'function') window.closePopup('review-form');
        if (typeof window.openPopup === 'function') window.openPopup('review-success');
        resetForm();
        loadReviewsList();
        return;
      }
      throw new Error(data.error || data.message || 'Error submitting review');
    } catch (error) {
      showFormError(error.message || 'An error occurred while submitting the review');
    } finally {
      if (!isReviewCooldownActive()) {
        submitReviewBtn.disabled = false;
        submitReviewBtn.textContent = 'Submit Review';
      }
    }
  }

  function setupFormSubmission() {
    if (!reviewForm) return;

    reviewForm.addEventListener('submit', async (e) => {
      e.preventDefault();

      if (isReviewCooldownActive()) {
        showFormError('Too many review submissions. Please wait a minute and try again');
        return;
      }

      if (!validateForm()) return;

      hideFormError();
      await processReviewSubmit();
    });
  }

  async function loadReviewsList() {
    if (!reviewsListContainer) return;
    
    // Show loading, hide everything else
    if (reviewsLoading) {
      reviewsLoading.classList.remove('reviews-section__loading--hidden');
    }
    if (reviewsError) reviewsError.style.display = 'none';
    if (reviewsEmptyState) {
      reviewsEmptyState.classList.remove('reviews-section__empty-state--visible');
    }
    if (reviewsSectionHeader) {
      reviewsSectionHeader.classList.add('reviews-section__header--hidden');
    }
    if (reviewsListContainer) {
      reviewsListContainer.classList.add('reviews-section__list--hidden');
    }
    if (reviewsBottomCta) {
      reviewsBottomCta.style.display = 'none';
    }

    try {
      const url = `${REVIEWS_LIST_URL}${productHandle ? `?handle=${encodeURIComponent(productHandle)}` : ''}`;
      const response = await fetch(url);
      
      if (!response.ok) {
        throw new Error('Error loading reviews');
      }
      
      const data = await response.json();
      
      // Hide loading
      if (reviewsLoading) {
        reviewsLoading.classList.add('reviews-section__loading--hidden');
      }
      
      if (!data.reviews || data.reviews.length === 0) {
        const isPage = section && section.classList.contains('reviews-section--page');
        if (isPage) {
          if (section) section.classList.remove('reviews-section--hidden');
          if (reviewsSectionHeader) reviewsSectionHeader.classList.remove('reviews-section__header--hidden');
          if (reviewsEmptyState) reviewsEmptyState.classList.add('reviews-section__empty-state--visible');
        } else {
          if (section) section.classList.add('reviews-section--hidden');
          if (reviewsEmptyState) reviewsEmptyState.classList.remove('reviews-section__empty-state--visible');
        }
        reviewsListContainer.replaceChildren();
        if (reviewsCount) reviewsCount.textContent = '0';
        if (reviewsLoading) reviewsLoading.classList.add('reviews-section__loading--hidden');
        if (reviewsBottomCta) {
          reviewsBottomCta.style.display = 'none';
        }
        return;
      }
      
      // Show section if reviews exist
      if (section) {
        section.classList.remove('reviews-section--hidden');
      }
      
      // Hide empty state, show header and list
      if (reviewsEmptyState) {
        reviewsEmptyState.classList.remove('reviews-section__empty-state--visible');
      }
      if (reviewsSectionHeader) {
        reviewsSectionHeader.classList.remove('reviews-section__header--hidden');
      }
      if (reviewsListContainer) {
        reviewsListContainer.classList.remove('reviews-section__list--hidden');
      }
      
      const isAllReviewsPage = section && section.classList.contains('reviews-section--page');
      renderReviewsList(data.reviews, isAllReviewsPage);
      
      if (reviewsCount) {
        reviewsCount.textContent = data.reviews.length;
      }

      if (reviewsBottomCta) {
        if (section && section.classList.contains('reviews-section--page')) {
          reviewsBottomCta.style.display = 'flex';
        } else if (data.reviews.length > 0) {
          reviewsBottomCta.style.display = 'flex';
        }
      }
      
    } catch (error) {
      console.error('Error loading reviews:', error);
      if (reviewsLoading) {
        reviewsLoading.classList.add('reviews-section__loading--hidden');
      }
      // Hide section on error (assume no reviews)
      if (section) {
        section.classList.add('reviews-section--hidden');
      }
      if (reviewsError) {
        reviewsError.textContent = error.message || 'Error loading reviews';
        reviewsError.style.display = 'block';
      }
      if (reviewsBottomCta) {
        reviewsBottomCta.style.display = 'none';
      }
    }
  }

  function renderReviewsList(reviews, showProductLink) {
    if (!reviewsListContainer) return;
    const reviewsHtml = reviews.map((review, reviewIndex) => {
      const userName = escapeHtml(review.user_name || 'Anonymous');
      const userInitial = userName.charAt(0).toUpperCase();
      const date = formatDate(review.submitted_at);
      const text = escapeHtml(review.text);
      const stars = renderStars(review.rating);
      const productTitle = review.product_title || review.product_name || '';
      const productHandle = review.product_handle || review.handle || '';
      const productUrl = review.product_url || (productHandle ? `/products/${encodeURIComponent(productHandle)}` : '');
      const orderNumber = review.order_number || review.orderNumber || '';
      const productLinkHtml = showProductLink && productTitle && productUrl
        ? `<a href="${escapeHtml(productUrl)}" class="reviews-section__review-product-link">${escapeHtml(productTitle)}</a>`
        : '';
      const orderNumberHtml = showProductLink && !productHandle && orderNumber
        ? `<div class="reviews-section__review-product-link reviews-section__review-order-number">Order #${escapeHtml(orderNumber)}</div>`
        : '';
      const photosHtml = review.photos && review.photos.length > 0
        ? `<div class="reviews-section__review-photos">
            ${review.photos.map((photo, photoIndex) => `
              <div class="reviews-section__review-photo" data-photo-url="${escapeHtml(photo)}">
                <img src="${escapeHtml(photo)}" alt="Review photo" loading="lazy">
              </div>
            `).join('')}
          </div>`
        : '';

      const dividerHtml = reviewIndex < reviews.length - 1 ? '<div class="reviews-section__divider"></div>' : '';
      return `
        <div class="reviews-section__review-block">
          <div class="reviews-section__review-container">
            <div class="reviews-section__review-header">
              <div class="reviews-section__review-user-block">
                <div class="reviews-section__review-avatar">${userInitial}</div>
                <div class="reviews-section__review-user-info">
                  <div class="reviews-section__review-user-name">${userName}</div>
                  <div class="reviews-section__review-date">${date}</div>
                </div>
              </div>
              <div class="reviews-section__review-rating-block">
                <div class="reviews-section__review-rating-label">${escapeHtml(ratingLabel)}</div>
                <div class="reviews-section__review-stars">${stars}</div>
              </div>
            </div>
            ${productLinkHtml}${orderNumberHtml}
            <div class="reviews-section__review-message">${text}</div>
          </div>
          ${photosHtml}
          ${dividerHtml}
        </div>
      `;
    }).join('');
    replaceContainerHtml(reviewsListContainer, reviewsHtml);
    
    setupPhotoViewer();
  }

  function collectAllPhotos() {
    if (!reviewsListContainer) return;
    allPhotos = collectPhotosFromContainer(reviewsListContainer);
  }

  function collectPhotosFromContainer(container) {
    const photos = [];
    const photoElements = container.querySelectorAll('.reviews-section__review-photo');
    photoElements.forEach((photoEl) => {
      const photoUrl = photoEl.getAttribute('data-photo-url');
      const img = photoEl.querySelector('img');
      if (photoUrl || img) {
        const url = photoUrl || (img ? (img.getAttribute('src') || img.src) : '');
        if (url) {
          photos.push({
            url,
            element: photoEl,
          });
        }
      }
    });
    return photos;
  }

  function buildReviewLightboxItem(photo, index) {
    const li = document.createElement('li');
    li.className = 'product-media-container product-media-container--image product-media-container--zoomable';
    li.setAttribute('ref', 'media[]');
    li.setAttribute('data-lightbox-portrait', 'true');
    li.id = `review-photo-lightbox-${index}`;

    const media = document.createElement('div');
    media.className = 'product-media';
    media.style.setProperty('--ratio', '1');

    const img = document.createElement('img');
    img.className = 'product-media__image';
    img.src = photo.url;
    img.alt = `Review photo ${index + 1}`;
    img.setAttribute('data_max_resolution', photo.url);
    img.width = 900;
    img.height = 900;
    img.loading = index === 0 ? 'eager' : 'lazy';

    media.appendChild(img);
    li.appendChild(media);
    return li;
  }

  function buildReviewThumbnail(photo, index) {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'button button-unstyled dialog-thumbnails-list__thumbnail';
    btn.setAttribute('data-lightbox-control', '');
    btn.setAttribute('data-lightbox-thumbnail', '');
    btn.setAttribute('on:click', `/handleThumbnailClick/${index}`);
    btn.setAttribute('on:pointerenter', `/handleThumbnailPointerEnter/${index}`);
    btn.setAttribute('aria-label', `Review photo ${index + 1}`);
    btn.style.setProperty('--aspect-ratio', '1');
    btn.style.setProperty('--gallery-aspect-ratio', '1');
    if (index === 0) btn.setAttribute('aria-selected', 'true');

    const img = document.createElement('img');
    img.src = photo.url;
    img.alt = `Review photo ${index + 1}`;
    img.loading = 'lazy';
    btn.appendChild(img);
    return btn;
  }

  function populateReviewsZoomDialog(photos) {
    if (!reviewsZoomDialog) return;
    const gallery = reviewsZoomDialog.querySelector('[ref="gallery"]');
    const thumbnails = reviewsZoomDialog.querySelector('[ref="thumbnails"]');
    const thumbContainer = reviewsZoomDialog.querySelector('.dialog-thumbnails-list-container');
    if (!gallery || !thumbnails) return;

    gallery.replaceChildren(...photos.map((photo, index) => buildReviewLightboxItem(photo, index)));
    thumbnails.replaceChildren(...photos.map((photo, index) => buildReviewThumbnail(photo, index)));
    if (thumbContainer) {
      thumbContainer.hidden = photos.length <= 1;
    }
  }

  function openReviewPhotoLightbox(photoIndex, event, photos = allPhotos) {
    if (!reviewsZoomDialog || photos.length === 0) return;

    populateReviewsZoomDialog(photos);
    const index = Math.max(0, Math.min(photoIndex, photos.length - 1));

    queueMicrotask(() => {
      reviewsZoomDialog.open(index, event);
    });
  }

  function setupPhotoViewer() {
    if (!reviewsListContainer || !reviewsZoomDialog) return;
    collectAllPhotos();

    const photoElements = reviewsListContainer.querySelectorAll('.reviews-section__review-photo');
    photoElements.forEach((photoEl) => {
      photoEl.addEventListener('click', (event) => {
        event.preventDefault();
        const reviewBlock = photoEl.closest('.reviews-section__review-block');
        const reviewPhotos = reviewBlock ? collectPhotosFromContainer(reviewBlock) : [];
        const photoIndex = reviewPhotos.findIndex((photo) => photo.element === photoEl);
        if (photoIndex === -1) return;
        openReviewPhotoLightbox(photoIndex, event, reviewPhotos);
      });
    });
  }

  function setupEventListeners() {
    if (reviewFormPopup) {
      reviewFormPopup.addEventListener('popup:close', () => resetForm());
    }
  }

  function init() {
    applyReviewFieldLimits();
    initReviewTextCounter();
    setupEventListeners();
    setupStarRating(productRatingStars);
    setupPhotoUpload();
    setupFormSubmission();
    loadReviewsList();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
}
